import { useCallback, useEffect, useState } from "react";

const isStandaloneNow = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const detectPlatform = () => {
  const ua = window.navigator.userAgent;

  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (!isIOS) return "other";

  // Only real Safari can install on iOS.
  // Chrome/Firefox/Edge on iOS and in-app browsers cannot.
  const isRealSafari =
    /Safari/.test(ua) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|Instagram|FBAN|FBAV|Line|Twitter/.test(ua);

  return isRealSafari ? "ios-safari" : "ios-blocked";
};

export function usePwaInstall() {
  const [prompt, setPrompt] = useState(() => window.__pwaPrompt || null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneNow);
  const [platform] = useState(detectPlatform);

  useEffect(() => {
    const onAvailable = () => setPrompt(window.__pwaPrompt);
    const onInstalled = () => {
      setPrompt(null);
      setIsStandalone(true);
    };

    const mq = window.matchMedia("(display-mode: standalone)");
    const onModeChange = (e) => setIsStandalone(e.matches);

    window.addEventListener("pwa:available", onAvailable);
    window.addEventListener("pwa:installed", onInstalled);
    mq.addEventListener("change", onModeChange);

    return () => {
      window.removeEventListener("pwa:available", onAvailable);
      window.removeEventListener("pwa:installed", onInstalled);
      mq.removeEventListener("change", onModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!prompt) return "unavailable";

    const { outcome } = await prompt.prompt();

    // The event is single-use — discard it either way.
    window.__pwaPrompt = null;
    setPrompt(null);

    return outcome; // 'accepted' | 'dismissed'
  }, [prompt]);

  return {
    canPrompt: !!prompt && !isStandalone, // show the real button
    needsManualSteps: !prompt && !isStandalone && platform === "ios-safari",
    isStandalone,
    platform,
    promptInstall,
  };
}
